/**
 * Une erreur de Lia dite a la personne. `reconnect` propose « Se reconnecter » ;
 * `settled` dit que le serveur a refuse AVANT tout travail (plafond du jour, acces
 * coupe) : rien a recuperer, « Récupérer la réponse » ne ferait que rejouer le refus.
 */
export class AskTakiError extends Error {
  constructor(message: string, readonly reconnect = false, readonly settled = false) {
    super(message);
  }
}

/** Refus pose avant tout travail et qu'un nouvel essai immediat ne leverait pas. */
export function refusedBeforeWork(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) return false;
  const source = 'details' in error && error.details && typeof error.details === 'object' && 'source' in error.details ? error.details.source : undefined;
  return (error.code === 'functions/resource-exhausted' && source === 'daily-limit') || error.code === 'functions/permission-denied';
}

/** Le serveur refuse un jeton revoque ou perime avant de commencer : un nouveau jeton suffit en general. */
export function needsFreshToken(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'code' in error && error.code === 'functions/unauthenticated' &&
    'details' in error && !!error.details && typeof error.details === 'object' && 'source' in error.details && error.details.source === 'token-refresh';
}

/** « Vous avez utilisé vos 100 demandes… de nouveau disponible demain à 02:00 », a l'heure de l'appareil. */
export function dailyLimitMessage(details: object, now = new Date()): string {
  const limit = 'limit' in details && typeof details.limit === 'number' ? details.limit : null;
  const reset = 'resetAt' in details && typeof details.resetAt === 'string' ? new Date(details.resetAt) : null;
  const used = limit ? `Vous avez utilisé vos ${limit} demandes à Lia pour aujourd’hui.` : 'Vous avez atteint la limite quotidienne de demandes à Lia.';
  if (!reset || Number.isNaN(reset.getTime())) return `${used} Elle sera de nouveau disponible demain.`;
  const time = reset.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const sameDay = reset.toDateString() === now.toDateString();
  return `${used} Elle sera de nouveau disponible ${sameDay ? 'aujourd’hui' : 'demain'} à ${time}.`;
}

export function askTakiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'details' in error &&
    error.details && typeof error.details === 'object' && 'source' in error.details &&
    'code' in error && error.code === 'functions/resource-exhausted') {
    if (error.details.source === 'tool-limit') return 'Cette réponse a atteint sa limite d’actions. Consultez les outils déjà utilisés, puis demandez à Lia de poursuivre.';
    if (error.details.source === 'daily-limit') return dailyLimitMessage(error.details);
  }
  if (error && typeof error === 'object' && 'details' in error &&
    error.details && typeof error.details === 'object' &&
    'source' in error.details && error.details.source === 'model-budget' &&
    'code' in error && error.code === 'functions/resource-exhausted') {
    if ('limit' in error.details && error.details.limit === 'output') {
      return 'La réponse du modèle a atteint sa limite de longueur. Précisez votre demande.';
    }
    return 'Cette réponse a atteint sa limite d’appels au modèle.';
  }
  if (error && typeof error === 'object' && 'details' in error &&
    error.details && typeof error.details === 'object' &&
    'source' in error.details && error.details.source === 'model' &&
    'code' in error) {
    if (error.code === 'functions/resource-exhausted') return 'Le modèle est surchargé. Réessayez dans un instant.';
    if (error.code === 'functions/unavailable') return 'Le modèle est momentanément indisponible. Réessayez plus tard.';
    if (error.code === 'functions/failed-precondition') return 'Le modèle a refusé cette demande. Reformulez votre question.';
  }
  const code = error && typeof error === 'object' && 'code' in error ? error.code : undefined;
  const source = error && typeof error === 'object' && 'details' in error &&
    error.details && typeof error.details === 'object' && 'source' in error.details ? error.details.source : undefined;
  if (code === 'functions/permission-denied' && source === 'ai-disabled') return 'L’accès IA est désactivé pour cette organisation.';
  if (code === 'functions/permission-denied') return 'L’accès à cette organisation a été refusé.';
  if (code === 'functions/unauthenticated' && source === 'token-refresh') return 'Votre session doit être renouvelée : reconnectez-vous pour continuer avec Lia.';
  if (code === 'functions/unauthenticated') return 'Reconnectez-vous pour parler à Lia.';
  if (code === 'functions/already-exists') return 'Cette demande est déjà en cours ou a été interrompue. Vérifiez vos fiches avant de réessayer.';
  if (code === 'functions/invalid-argument') return 'La demande est invalide. Reformulez-la puis réessayez.';
  if (code === 'functions/failed-precondition' && source === 'configuration') return 'Lia n’est pas configurée sur le serveur.';
  if (code === 'functions/cancelled') return 'Réponse interrompue. Une action déjà lancée peut se terminer.';
  return 'Lia ne répond pas pour le moment. Réessayez.';
}
